import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Folder, Pin, PinOff, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Menüstruktur gemäß Organisationsmappe: Bereiche → (Ordner →) Punkte
export const NAV_TREE = [
  { key: "start", label: null, items: ["dashboard", "kunden_status"] },
  { key: "massnahmen", label: "Maßnahmen", items: ["avgs", "kunden_zertifikate", "kunden_archiv", { key: "f_wb", label: "Weiterbildung (FbW)", items: ["weiterbildung", "wb_chat"] }] },
  { key: "kalender", label: "Kalender", items: ["kalender", "termine", "terminplanung", "vertretungsplan", "wochenuebersicht"] },
  { key: "personal", label: "Personal", items: ["coaches", "dozenten", "mitarbeiter"] },
  { key: "verwaltung", label: "Verwaltung", items: ["traegerbestaetigung", "lizenzen", "postfach", "vertraege", "zertifikate", "email_log"] },
  { key: "controlling", label: "Controlling", items: [{ key: "f_ziele", label: "Mitarbeiterziele", items: ["kpi", "team_pm"] }, "entries", "meine_kapazitaet", "coach_capacity", "wochenkontrolle", "management"] },
  { key: "audit", label: "Internes Audit", items: ["umfrage_ergebnisse", "unterlagen", "interne_audit", "verbleibsquote"] },
  { key: "abrechnung", label: "Abrechnung", items: ["abr_avgs", "abr_zahlungen", "abr_abschlaege", "abr_wb", "abr_datev"] },
  { key: "traeger", label: "Träger", items: ["traeger_stammdaten", "traeger_adresse", "traeger_brand", "traeger_vorlagen", "traeger_signatur", "traeger_siegel", "traeger_ziele", "traeger_zulassungen", "traeger_cc"] },
  { key: "plattform", label: "Plattform", items: ["plattform"] },
];

const isActive = (item, loc) => {
  const [path, query] = item.to.split("?");
  if (query) return loc.pathname === path && (loc.search === `?${query}` || (!loc.search && item.defaultFor));
  if ((item.excl || []).some((x) => loc.pathname.startsWith(x))) return false;
  return loc.pathname === path || (path !== "/" && loc.pathname.startsWith(`${path}/`));
};

function Item({ item, loc, nested, label, fav, onPin, fid = "" }) {
  const Icon = item.icon;
  const active = isActive(item, loc);
  return (
    <div className="group relative">
      <NavLink to={item.to} data-testid={`${item.testId}${fid}`}
        className={`flex items-center gap-3 py-2 mb-0.5 text-sm transition-colors ${nested ? "pl-10 pr-8" : "pl-6 pr-8"} ${active ? "brand-active text-white" : "text-slate-700 hover:bg-slate-100"}`}>
        <Icon size={nested ? 13 : 15} strokeWidth={1.5} className="shrink-0" />
        <span className={`truncate ${nested ? "text-[13px]" : "font-medium"}`}>{label || item.label}</span>
        {item.badge > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`nav-badge-${item.key}${fid}`}>{item.badge}</span>}
      </NavLink>
      {onPin && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPin(item.key); }} title={fav ? "Von Favoriten lösen" : "An Favoriten anheften"}
          data-testid={`nav-pin-${item.key}${fid}`}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 transition-opacity ${fav ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"} ${active ? "text-white/80 hover:text-white" : "text-slate-400 hover:text-[#0a788c]"}`}>
          {fav ? <PinOff size={12} /> : <Pin size={12} />}
        </button>
      )}
    </div>
  );
}

function FolderNode({ node, byKey, loc, labelFor, favs, onPin }) {
  const children = node.items.map((k) => byKey[k]).filter(Boolean);
  const containsActive = children.some((c) => isActive(c, loc));
  const [open, setOpen] = useState(containsActive);
  useEffect(() => { if (containsActive) setOpen(true); }, [containsActive]);
  if (!children.length) return null;
  return (
    <div>
      <button onClick={() => setOpen(!open)} data-testid={`nav-folder-${node.key}`}
        className={`w-full flex items-center gap-3 pl-6 pr-3 py-2 mb-0.5 text-sm font-medium transition-colors ${containsActive ? "text-slate-900" : "text-slate-700"} hover:bg-slate-100`}>
        <Folder size={15} strokeWidth={1.5} className="shrink-0" />
        <span className="flex-1 text-left truncate">{node.label}</span>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {open && <div className="border-l border-slate-200 ml-[31px]">{children.map((c) => <Item key={c.key} item={c} loc={loc} nested label={labelFor(c)} fav={favs.includes(c.key)} onPin={onPin} />)}</div>}
    </div>
  );
}

export default function SidebarNav({ items, labelFor }) {
  const loc = useLocation();
  const { user, setUser } = useAuth();
  const favs = (user?.nav_favorites || []).filter((k) => items.some((i) => i.key === k));
  const onPin = async (key) => {
    const next = favs.includes(key) ? favs.filter((k) => k !== key) : [...favs, key];
    if (next.length > 12) return toast.error("Maximal 12 Favoriten");
    setUser((u) => ({ ...u, nav_favorites: next }));
    try { await api.put("/users/me/nav-favorites", { keys: next }); } catch (e) { toast.error("Favoriten konnten nicht gespeichert werden"); }
  };
  const byKey = Object.fromEntries(items.map((i) => [i.key, i]));
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nav_collapsed") || "{}"); } catch { return {}; }
  });
  const toggle = (k) => setCollapsed((c) => { const n = { ...c, [k]: !c[k] }; localStorage.setItem("nav_collapsed", JSON.stringify(n)); return n; });
  const flat = (node) => (typeof node === "string" ? [node] : node.items);

  const favBlock = favs.length > 0 && (
    <div key="fav" className="mb-3" data-testid="nav-section-favoriten">
      <div className="flex items-center gap-1.5 pl-3 pr-3 py-2 mb-1 text-[13px] font-bold border-l-4 border-amber-400 bg-amber-50 text-slate-900"><Star size={12} className="text-amber-500" /> Favoriten</div>
      {favs.map((k) => <Item key={k} item={byKey[k]} loc={loc} label={labelFor(byKey[k])} fav onPin={onPin} fid="-fav" />)}
    </div>
  );

  return [favBlock, ...NAV_TREE.map((sec) => {
    const keys = sec.items.flatMap(flat).filter((k) => byKey[k]);
    if (!keys.length) return null;
    const secActive = keys.some((k) => isActive(byKey[k], loc));
    const isOpen = !sec.label || !collapsed[sec.key] || secActive;
    return (
      <div key={sec.key} className={sec.label ? "mt-4" : "mb-1"} data-testid={`nav-section-${sec.key}`}>
        {sec.label && (
          <button onClick={() => !secActive && toggle(sec.key)} data-testid={`nav-group-${sec.key}`}
            className={`w-full flex items-center justify-between pl-3 pr-3 py-2 mb-1 text-[13px] font-bold border-l-4 transition-colors ${secActive ? "brand-sec-active text-slate-900" : "brand-sec bg-slate-50 text-slate-800 hover:bg-slate-100"}`}>
            {sec.label}
            <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${isOpen ? "" : "-rotate-90"}`} />
          </button>
        )}
        {isOpen && sec.items.map((node) => (typeof node === "string"
          ? byKey[node] && <Item key={node} item={byKey[node]} loc={loc} label={labelFor(byKey[node])} fav={favs.includes(node)} onPin={onPin} />
          : <FolderNode key={node.key} node={node} byKey={byKey} loc={loc} labelFor={labelFor} favs={favs} onPin={onPin} />))}
      </div>
    );
  })];
}
